"use client";

import NextLink from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { AuthLayout } from "@/components/ui/auth-layout";
import { Button } from "@/components/ui/button";
import { Description, Field, FieldGroup, Label } from "@/components/ui/fieldset";
import { Heading } from "@/components/ui/heading";
import { Input } from "@/components/ui/input";
import { Text, TextLink } from "@/components/ui/text";
import { ApiError, auth } from "@/lib/api";

const PASSWORD_CHARSET =
  "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%^&*-_=+";

function generateStrongPassword(length = 20): string {
  const out = new Array<string>(length);
  const buf = new Uint32Array(length);
  crypto.getRandomValues(buf);
  for (let i = 0; i < length; i++) {
    out[i] = PASSWORD_CHARSET[buf[i] % PASSWORD_CHARSET.length];
  }
  return out.join("");
}

export default function SignupPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signupAllowed, setSignupAllowed] = useState(true);

  useEffect(() => {
    auth
      .providers()
      .then((p) => setSignupAllowed(p.signup))
      .catch(() => setSignupAllowed(true)); // fail open in UI; the API still enforces
  }, []);

  function onGenerate() {
    const pwd = generateStrongPassword(20);
    setPassword(pwd);
    setShowPassword(true);
    setPasswordCopied(false);
  }

  async function onCopy() {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      setPasswordCopied(true);
      window.setTimeout(() => setPasswordCopied(false), 2000);
    } catch {
      /* clipboard not available */
    }
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await auth.signup(email, password);
      router.push(`/login?email=${encodeURIComponent(email)}`);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Unexpected error");
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-sm">
        <NextLink href="/" className="mb-6 flex items-center gap-2">
          <div className="flex size-8 items-center justify-center rounded-lg bg-zinc-900 text-sm font-bold text-white dark:bg-white dark:text-zinc-900">
            I
          </div>
          <span className="text-sm font-semibold text-zinc-950 dark:text-white">
            INI Assistant
          </span>
        </NextLink>
        <Heading>Create account</Heading>
        <Text className="mt-1">
          Already have one? <TextLink href="/login">Log in</TextLink>
        </Text>

        {!signupAllowed && (
          <p className="mt-6 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
            Sign-ups are currently disabled while INI Assistant is in development. Check back
            soon.
          </p>
        )}

        {signupAllowed && (
          <div className="mt-6">
            <GoogleSignInButton />
          </div>
        )}

        {signupAllowed && (
        <form onSubmit={onSubmit} className="mt-4">
          <FieldGroup>
            <Field>
              <Label>Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </Field>
            <Field>
              <div className="flex items-center justify-between">
                <Label>Password</Label>
                <button
                  type="button"
                  onClick={onGenerate}
                  className="text-xs font-medium text-zinc-600 underline decoration-dotted underline-offset-4 hover:text-zinc-950 dark:text-zinc-400 dark:hover:text-white"
                >
                  Generate strong password
                </button>
              </div>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordCopied(false);
                  }}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="!pr-24 !font-mono"
                />
                <div className="absolute inset-y-0 right-1 flex items-center gap-0.5">
                  {password && (
                    <button
                      type="button"
                      onClick={onCopy}
                      title="Copy password"
                      className="rounded px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-white"
                    >
                      {passwordCopied ? "Copied" : "Copy"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="rounded px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-white"
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
              <Description>
                At least 8 characters. Save it somewhere safe if you generate one — we can&apos;t
                recover it for you.
              </Description>
            </Field>
          </FieldGroup>

          <div className="mt-6">
            <Button type="submit" disabled={submitting} className="w-full">
              {submitting ? "Creating…" : "Create account"}
            </Button>
          </div>

          {error && (
            <p className="mt-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
              {error}
            </p>
          )}
        </form>
        )}
      </div>
    </AuthLayout>
  );
}
